import { handleWarehouseError, warehouseService } from "~/api/warehouse";
import { AsyncAppThunk } from "~/app/store";
import { SetUserMessageAction } from "~/redux/actions/site";
import {
  ToteEventDto,
  PTLEventDto,
  ToteDto,
  AutostoreEvent
} from "~/types/api";

import { MixedEventData, StructuredEvent } from "./andon.types";

export function hasEventType(
  event: MixedEventData
): event is MixedEventData & { eventType: string } {
  if (typeof event !== "object" || event === null) return false;
  return "eventType" in event;
}

export function isAutostoreEvent(event: unknown): event is AutostoreEvent {
  return typeof event === "object" && event !== null && "case" in event;
}

export function hasTote(
  event: MixedEventData
): event is ToteEventDto & { tote: ToteDto } {
  return "tote" in event;
}

export function hasText(
  event: MixedEventData
): event is PTLEventDto & { data: { text: string } } {
  return "data" in event && "text" in event.data;
}

export function hasDeviceId(
  event: MixedEventData
): event is PTLEventDto & { data: { deviceId: string } } {
  return "deviceId" in event;
}

export const sendUserMessages =
  (args: {
    fcId: Guid;
    recipientIds: string[];
    message: string;
  }): AsyncAppThunk =>
  async (dispatch) => {
    const { fcId, recipientIds, message } = args;

    try {
      for (const recipientId of recipientIds) {
        await warehouseService.post(`/public-api/v1/users/message`, {
          FulfillmentCenterId: fcId,
          UserId: recipientId,
          Message: message
        });
      }
    } catch (err) {
      handleWarehouseError(err, (message2) => {
        dispatch<SetUserMessageAction>({
          type: "site/SET_USER_MESSAGE",
          payload: {
            title: message2,
            severity: "error"
          }
        });
      });
    }
  };

export const portIdFromEvent = (e: StructuredEvent): string | undefined => {
  // SignalRPickEventDto
  if ("portId" in e && typeof e.portId === "number") {
    return String(e.portId);
  }

  if ("event" in e && typeof e.event === "object" && e.event !== null) {
    if ("portId" in e.event && e.event.portId != null) {
      return String(e.event.portId);
    }

    if ("sourcePortId" in e.event && e.event.sourcePortId != null) {
      return String(e.event.sourcePortId);
    }
  }

  return undefined;
};

export const gridIdFromEvent = (e: StructuredEvent): string | undefined => {
  if ("event" in e && typeof e.event === "object" && e.event !== null) {
    if ("gridId" in e.event && typeof e.event.gridId === "string") {
      return e.event.gridId;
    }
  }

  return undefined;
};

export const binIdFromEvent = (e: StructuredEvent): string | undefined => {
  // SignalRPickEventDto
  if (
    "pick" in e &&
    typeof e.pick === "object" &&
    e.pick.pickBin?.binId != null
  ) {
    return String(e.pick.pickBin.binId);
  }

  if ("event" in e && typeof e.event === "object" && e.event !== null) {
    if ("binId" in e.event && e.event.binId != null) {
      return String(e.event.binId);
    }

    if ("binGuid" in e.event && e.event.binGuid != null) {
      return String(e.event.binGuid);
    }

    if ("sourceBinId" in e.event && e.event.sourceBinId != null) {
      return String(e.event.sourceBinId);
    }
  }

  return undefined;
};

export const workstationIdFromEvent = (
  e: StructuredEvent
): string | undefined => {
  if ("workstationId" in e && typeof e.workstationId === "string") {
    return e.workstationId;
  }
  if (
    "event" in e &&
    typeof e.event === "object" &&
    e.event !== null &&
    "workstationId" in e.event &&
    typeof e.event.workstationId === "string"
  ) {
    return e.event.workstationId;
  }
  return undefined;
};
